// src/pages/reports/HsnSummaryReport.js
import React, { useState } from "react";
import Layout from "../../components/Layout";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  doc,
  getDoc
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";


// --- PREMIUM UI TOKENS (Standardized for Reports) ---
const UI_THEME = {
  bg: "#f8fafc",
  surface: "#ffffff",
  primary: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  secondary: "#f1f5f9",
  accent: "#10b981",
  text: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  shadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
};

const filterCardStyle = {
  background: UI_THEME.surface,
  padding: "24px",
  borderRadius: "16px",
  boxShadow: UI_THEME.shadow,
  border: `1px solid ${UI_THEME.border}`,
  display: "flex",
  gap: "20px",
  alignItems: "flex-end",
  marginBottom: "32px",
  flexWrap: "wrap",
};

const inputGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const labelStyle = {
  fontSize: "12px",
  fontWeight: 800,
  color: UI_THEME.muted,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const inputStyle = {
  padding: "10px 14px",
  borderRadius: "10px",
  border: `1px solid ${UI_THEME.border}`,
  fontSize: "14px",
  fontWeight: 600,
  color: UI_THEME.text,
  outline: "none",
  minWidth: "160px",
};

const primaryBtn = {
  padding: "10px 24px",
  borderRadius: "10px",
  background: UI_THEME.primary,
  color: "#fff",
  border: "none",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.4)",
};

const secondaryBtn = {
  padding: "10px 20px",
  borderRadius: "10px",
  background: "#f1f5f9",
  color: "#475569",
  border: "none",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
};

const tableHeaderStyle = {
  padding: "16px 20px",
  background: "#f8fafc",
  color: "#64748b",
  fontSize: "11px",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderBottom: `2px solid ${UI_THEME.border}`,
  textAlign: "left",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const tableCellStyle = {
  padding: "14px 20px",
  fontSize: "14px",
  color: UI_THEME.text,
  borderBottom: `1px solid #f1f5f9`,
};

function toStart(d){ const x=new Date(d);x.setHours(0,0,0,0);return Timestamp.fromDate(x);}
function toEnd(d){ const x=new Date(d);x.setHours(23,59,59,999);return Timestamp.fromDate(x);}
function dmy(ts){ return ts.toDate().toLocaleDateString("en-IN"); }

function formatQty(v) {
  return Number(v || 0).toLocaleString(undefined, { 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 3,
    useGrouping: false 
  });
}

export default function HsnSummaryReport() {
  const today = new Date().toISOString().slice(0,10);
  const [fromDate,setFromDate]=useState(today);
  const [toDate,setToDate]=useState(today);
  const [party,setParty]=useState("");
  const [rows,setRows]=useState([]);
  const [clientId, setClientId] = useState("");

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        setClientId(profile.clientId || "mdsons");
      }
    });
    return () => unsub();
  }, []);

  const loadReport = async () => {
    const q = query(
      collection(db,"orderLines"),
      where("clientId", "==", clientId),
      where("status","==","APPROVED"),
      where("approvedAt",">=",toStart(fromDate)),
      where("approvedAt","<=",toEnd(toDate))
    );





    const snap = await getDocs(q);
    const map = new Map();

    snap.forEach(d=>{
      const r=d.data();
      if(party && r.partyName!==party) return;

      const key = `${r.displayOrderId}__${r.hsn}`;
      if(!map.has(key)){
        map.set(key,{
          Date:dmy(r.approvedAt),
          Party:r.partyName,
          Vehicle:r.vehicleNo,
          Challan:r.displayOrderId,
          HSN:r.hsn,
          Category:r.description || "CIGARETTES",
          Qty:0,
          Basic:0,
          GST:0,
          Cess:0,
          Addl:0,
          Eway:0
        });
      }
	  
	  
	  
	  
	  
      const g=map.get(key);
      g.Qty+=r.qty||0;
      g.Basic+=r.basicAmount||0;
      g.GST+=r.gstAmount||0;
      g.Cess+=r.cessAmount||0;
      g.Addl+=r.addlCessAmount||0;
      g.Eway+=r.ewayLineTotal||0;
    });





    const sortedRows = Array.from(map.values()).sort((a, b) => {
  // 1️⃣ Sort by Challan (displayOrderId) ASC
  if (a.Challan < b.Challan) return -1;
  if (a.Challan > b.Challan) return 1;

  // 2️⃣ Inside same Challan → HSN ASC
  return String(a.HSN).localeCompare(String(b.HSN));
});

setRows(sortedRows);

  };


const exportExcel = () => {
  if (!rows.length) {
    alert("No data to export");
    return;
  }

  const excelRows = rows.map(r => ({
    Date: r.Date,
    Party: r.Party,
    Vehicle: r.Vehicle,
    Challan: r.Challan,
    HSN: r.HSN,
    Category: r.Category,
    Qty: r.Qty,
    BasicAmount: r.Basic,
    GSTAmount: r.GST,
    CessAmount: r.Cess,
    AddlCessAmount: r.Addl,
    EwayAmount: r.Eway,
  }));

  const ws = XLSX.utils.json_to_sheet(excelRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "HSN Summary");

  const buffer = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
  });

  saveAs(
    new Blob([buffer], { type: "application/octet-stream" }),
    `HSN_Summary_${fromDate}_to_${toDate}.xlsx`
  );
};



  return (
    <Layout>
      <div style={{ padding: "32px", background: UI_THEME.bg, minHeight: "calc(100vh - 80px)" }}>
        
        {/* PAGE HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "32px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: 800, color: UI_THEME.text, margin: 0 }}>HSN Summary Report</h1>
            <p style={{ color: UI_THEME.muted, fontWeight: 500, marginTop: "4px" }}>Tax compliance & HSN breakdown for regulatory filing</p>
          </div>
          <button onClick={exportExcel} style={secondaryBtn}>
            📥 Export to Excel
          </button>
        </div>

        {/* FILTER CARD */}
        <div style={filterCardStyle}>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Date From</label>
            <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Date To</label>
            <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ ...inputGroupStyle, flex: 1, minWidth: "250px" }}>
            <label style={labelStyle}>Party Name (Optional)</label>
            <input 
              placeholder="Search by party..." 
              onChange={e=>setParty(e.target.value)} 
              style={{ ...inputStyle, width: "100%" }} 
            />
          </div>
          <button onClick={loadReport} style={primaryBtn}>Generate</button>
        </div>

        {/* DATA TABLE CARD */}
        <div style={{ 
          background: UI_THEME.surface, 
          borderRadius: "16px", 
          boxShadow: UI_THEME.shadow, 
          border: `1px solid ${UI_THEME.border}`,
          overflow: "hidden"
        }}>
          <div style={{ overflowX: "auto", maxHeight: "600px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1200px" }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Date</th>
                  <th style={tableHeaderStyle}>Party</th>
                  <th style={tableHeaderStyle}>Vehicle</th>
                  <th style={tableHeaderStyle}>Challan</th>
                  <th style={tableHeaderStyle}>HSN</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "center" }}>Category</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "center" }}>Qty</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Basic</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>GST</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Cess</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Addl</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Eway</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="12" style={{ padding: "100px", textAlign: "center", color: UI_THEME.muted }}>
                      <div style={{ fontSize: "24px", marginBottom: "16px" }}>📭</div>
                      No HSN summary data for this range.
                    </td>
                  </tr>
                ) : (
                  rows.map((r,i)=>(
                    <tr key={i} style={{ 
                      background: i % 2 === 0 ? "#fff" : "#fafafa",
                      transition: "background 0.2s"
                    }}>
                      <td style={tableCellStyle}>{r.Date}</td>
                      <td style={{ ...tableCellStyle, fontWeight: 700 }}>{r.Party}</td>
                      <td style={tableCellStyle}>{r.Vehicle}</td>
                      <td style={tableCellStyle}>{r.Challan}</td>
                      <td style={{ ...tableCellStyle, color: "#4f46e5", fontWeight: 800 }}>{r.HSN}</td>
                      <td style={{ ...tableCellStyle, textAlign: "center" }}>
                        <span style={{ background: "#f1f5f9", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>
                          {r.Category}
                        </span>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{formatQty(r.Qty)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.Basic.toFixed(2)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.GST.toFixed(2)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.Cess.toFixed(2)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.Addl.toFixed(2)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.Eway.toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {rows.length > 0 && (
            <div style={{ padding: "16px 24px", borderTop: `1px solid ${UI_THEME.border}`, background: "#f8fafc" }}>
               <span style={{ fontSize: "13px", fontWeight: 600, color: UI_THEME.muted }}>
                Total Summary Rows: {rows.length}
              </span>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
          
		  
		  



		  
		  

